import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Megaphone, Pause, Play, X, Eye } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  active: "bg-[#FFD600] text-[#0A0A0A]",
  paused: "bg-[#5C5C5C] text-white",
  completed: "bg-[#0A0A0A] text-[#FFD600]",
  cancelled: "bg-[#E53935] text-white",
};
const STATUS_LABEL = {
  active: "ACTIVA",
  paused: "PAUSADA",
  completed: "COMPLETADA",
  cancelled: "CANCELADA",
};

export default function Campaigns() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setList(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando campañas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/campaigns/${id}/status`, { status });
      toast.success(`Campaña ${STATUS_LABEL[status].toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar estado");
    }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar campaña? Se cancelarán publicaciones programadas.")) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("Campaña eliminada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">OPERATIVO · CAMPAÑAS · IA</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-none">Campañas Autónomas</h1>
          <p className="font-mono-data text-sm text-[#5C5C5C] mt-3 max-w-xl">
            CIA Marketing® planea, genera y publica tus campañas multi-red. Tú das el brief; nosotros operamos.
          </p>
        </div>
        <Link
          to="/dashboard/campaigns/new"
          className="bg-[#E53935] text-white font-mono-mark text-[10px] px-5 py-3 hover:bg-[#0A0A0A] flex items-center gap-2"
          data-testid="new-campaign-btn"
        >
          <Plus className="w-3 h-3" /> NUEVA CAMPAÑA
        </Link>
      </div>

      {loading ? (
        <div className="font-mono-data text-sm text-[#5C5C5C]">Cargando...</div>
      ) : list.length === 0 ? (
        <div className="border border-dashed border-[#0A0A0A] p-10 text-center">
          <Megaphone className="w-10 h-10 mx-auto text-[#0A0A0A]" />
          <div className="font-display text-2xl mt-4">Aún no tienes campañas</div>
          <p className="font-mono-data text-sm text-[#5C5C5C] mt-2 max-w-md mx-auto">
            Crea tu primera campaña autónoma. Define marca, fechas, tono, audiencia y cadencia — CIA hace el resto.
          </p>
          <Link to="/dashboard/campaigns/new" className="inline-block mt-6 bg-[#E53935] text-white font-mono-mark text-[10px] px-5 py-3 hover:bg-[#0A0A0A]">
            CREAR PRIMERA CAMPAÑA
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((c) => (
            <div key={c.id} className="border border-[#0A0A0A] bg-white p-5 flex flex-col gap-3" data-testid={`campaign-card-${c.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-mono-mark text-[9px] text-[#5C5C5C]">
                    {new Date(c.start_date).toLocaleDateString("es-MX")} → {new Date(c.end_date).toLocaleDateString("es-MX")} · {c.duration_days} DÍAS
                  </div>
                  <h3 className="font-display text-2xl mt-1 leading-tight truncate">{c.name}</h3>
                </div>
                <span className={`font-mono-mark text-[9px] px-2 py-1 ${STATUS_COLORS[c.status]}`}>
                  {STATUS_LABEL[c.status]}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 font-mono-mark text-[9px]">
                <span className="bg-[#0A0A0A] text-white px-2 py-1">{c.objective?.toUpperCase()}</span>
                <span className="bg-[#F4F4F0] border border-[#0A0A0A] px-2 py-1">{c.tone?.toUpperCase()}</span>
                {(c.networks || []).map((n) => (
                  <span key={n} className="border border-[#5C5C5C] text-[#5C5C5C] px-2 py-1">{n}</span>
                ))}
              </div>

              {c.progress && (
                <div className="font-mono-data text-xs">
                  <div className="flex justify-between text-[#5C5C5C]">
                    <span>{c.progress.published} publicados · {c.progress.scheduled} programados · {c.progress.failed} fallidos</span>
                    <span>{c.progress.percent}%</span>
                  </div>
                  <div className="h-1 bg-[#F4F4F0] mt-1">
                    <div className="h-full bg-[#E53935]" style={{ width: `${c.progress.percent}%` }} />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-1">
                <Link
                  to={`/dashboard/campaigns/${c.id}`}
                  className="font-mono-mark text-[9px] px-3 py-2 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white flex items-center gap-1"
                  data-testid={`campaign-view-${c.id}`}
                >
                  <Eye className="w-3 h-3" /> VER DETALLE
                </Link>
                {c.status === "active" && (
                  <button onClick={() => updateStatus(c.id, "paused")} className="font-mono-mark text-[9px] px-3 py-2 border border-[#5C5C5C] hover:bg-[#5C5C5C] hover:text-white flex items-center gap-1" data-testid={`campaign-pause-${c.id}`}>
                    <Pause className="w-3 h-3" /> PAUSAR
                  </button>
                )}
                {c.status === "paused" && (
                  <button onClick={() => updateStatus(c.id, "active")} className="font-mono-mark text-[9px] px-3 py-2 border border-[#FFD600] bg-[#FFD600] hover:bg-[#0A0A0A] hover:text-[#FFD600] flex items-center gap-1" data-testid={`campaign-resume-${c.id}`}>
                    <Play className="w-3 h-3" /> REANUDAR
                  </button>
                )}
                {c.status !== "completed" && c.status !== "cancelled" && (
                  <button onClick={() => updateStatus(c.id, "cancelled")} className="font-mono-mark text-[9px] px-3 py-2 border border-[#E53935] text-[#E53935] hover:bg-[#E53935] hover:text-white flex items-center gap-1" data-testid={`campaign-cancel-${c.id}`}>
                    <X className="w-3 h-3" /> CANCELAR
                  </button>
                )}
                <button onClick={() => remove(c.id)} className="font-mono-mark text-[9px] px-3 py-2 text-[#5C5C5C] hover:text-[#E53935]" data-testid={`campaign-delete-${c.id}`}>
                  ELIMINAR
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
